<template>
    <div>
        <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
            <v-toolbar color="#37474F" dark>
                <v-toolbar-title>전면 이벤트 관리</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn
                    color="cyan"
                    small
                    fab
                    @click="OnClick_CreateEvent"
                >
                    <v-icon color="white">mdi-plus</v-icon>
                </v-btn>
            </v-toolbar>

            <v-list>
                <v-data-table
                    :footer-props="{'items-per-page-options':[20, 50, -1], 'items-per-page': 20}"
                    :headers="headers"
                    :items="event_list"
                    :loading="loadingData"
                    class="elevation-1"
                >
                    <template v-slot:item.THUMB_URL="{ item }">
                        <img 
                            :src="'https://content-filecenter.s3.ap-northeast-2.amazonaws.com/' + item.THUMB_URL" 
                            alt="썸네일" 
                            style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;"
                        />
                    </template>
                    <template v-slot:item.TITLE="{ item }">
                        <span class="font-weight-medium">{{ item.TITLE }}</span>
                    </template>
                    <template v-slot:item.LINK_TYPE="{ item }">
                        <span class="text-body-2">{{ getLinkTypeLabel(item.LINK_TYPE) }}</span>
                    </template>
                    <template v-slot:item.IS_IMPORTANT="{ item }">
                        <v-icon 
                            small 
                            :color="item.IS_IMPORTANT === 'Y' ? 'red' : 'grey lighten-1'"
                        >
                            {{ item.IS_IMPORTANT === 'Y' ? 'mdi-star' : 'mdi-star-outline' }}
                        </v-icon>
                    </template>
                    <template v-slot:item.PRIORITY="{ item }">
                        <span class="text-body-2">{{ item.PRIORITY }}</span>
                    </template>
                    <template v-slot:item.ENABLE_YN="{ item }">
                        <v-icon 
                            small 
                            :color="item.ENABLE_YN === 'Y' ? 'green' : 'red'"
                        >
                            {{ item.ENABLE_YN === 'Y' ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                        <span class="ml-1 text-body-2">{{ item.ENABLE_YN === 'Y' ? '활성화' : '비활성화' }}</span>
                    </template>
                    <template v-slot:item.CREATE_DT="{ item }">
                        <span class="text-body-2">{{ formatDate(item.CREATE_DT) }}</span>
                    </template>
                    <template v-slot:item.edit="{ item }">
                        <v-btn
                            color="primary"
                            small
                            outlined
                            @click="OnClick_EditEvent(item)"
                        >
                            <v-icon small left>mdi-pencil</v-icon>
                            편집
                        </v-btn>
                    </template>
                </v-data-table>
            </v-list>
        </v-card>

        <!-- 이벤트 생성/수정 다이얼로그 -->
        <v-dialog
            v-model="dialog_event"
            persistent
            max-width="800"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            @keydown.esc="CloseEventDialog"
        >
            <v-card>
                <v-card-title class="text-h5 primary white--text">
                    <v-icon left color="white">{{ dialog_mode_edit ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                    {{ dialog_mode_edit ? '이벤트 수정' : '이벤트 등록' }}
                </v-card-title>

                <v-card-text class="pa-6">
                    <!-- 기본 정보 섹션 -->
                    <div class="mb-6">
                        <div class="d-flex align-center mb-4">
                            <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                            <h3 class="text-h6 font-weight-bold">기본 정보</h3>
                        </div>
                        <v-divider class="mb-4"></v-divider>
                        <v-row>
                            <v-col cols="12" md="4">
                                <div class="mb-3">
                                    <label class="text-caption grey--text text--darken-1 mb-1 d-block">썸네일 이미지</label>
                                    <v-img
                                        v-if="dialog_thumb_url"
                                        :src="'https://content-filecenter.s3.ap-northeast-2.amazonaws.com/' + dialog_thumb_url"
                                        alt="썸네일 미리보기"
                                        style="width: 100%; height: 150px; object-fit: cover; border-radius: 8px; border: 2px solid #e0e0e0;"
                                        class="mb-3"
                                    />
                                    <v-file-input
                                        label="이미지 선택"
                                        accept="image/*"
                                        v-model="dialog_thumb_file"
                                        prepend-icon="mdi-image"
                                        outlined
                                        dense
                                        hide-details
                                        @change="handleFileChange"
                                    />
                                </div>
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="dialog_title"
                                    label="타이틀 *"
                                    outlined
                                    dense
                                    class="mb-3"
                                    maxlength="32"
                                    counter
                                    prepend-inner-icon="mdi-format-title"
                                />
                                <v-text-field
                                    v-model="dialog_desc_text"
                                    label="설명 *"
                                    outlined
                                    dense
                                    class="mb-3"
                                    maxlength="128"
                                    counter
                                    prepend-inner-icon="mdi-text"
                                />
                                <v-text-field
                                    v-model="dialog_button_text"
                                    label="버튼 문구 *"
                                    outlined
                                    dense
                                    maxlength="24"
                                    counter
                                    prepend-inner-icon="mdi-cursor-pointer"
                                />
                            </v-col>
                        </v-row>
                    </div>

                    <!-- 링크 설정 섹션 -->
                    <div class="mb-6">
                        <div class="d-flex align-center mb-4">
                            <v-icon color="primary" class="mr-2">mdi-link-variant</v-icon>
                            <h3 class="text-h6 font-weight-bold">링크 설정</h3>
                        </div>
                        <v-divider class="mb-4"></v-divider>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="dialog_link_type"
                                    :items="link_type_options"
                                    item-text="label"
                                    item-value="value"
                                    label="링크 타입"
                                    outlined
                                    dense
                                    prepend-inner-icon="mdi-tag"
                                    @change="onLinkTypeChange"
                                />
                            </v-col>
                            <!-- 오퍼월 타입: 지정된 목록에서만 선택 (자유 입력 불가) -->
                            <v-col v-if="dialog_link_type === 'OFFERWALL'" cols="12" md="8">
                                <v-select
                                    v-model="dialog_link_path"
                                    :items="offerwall_options"
                                    label="오퍼월 선택"
                                    outlined
                                    dense
                                    hint="오퍼월은 목록에서만 선택할 수 있습니다"
                                    persistent-hint
                                    prepend-inner-icon="mdi-map-marker-path"
                                />
                            </v-col>
                            <!-- 캠페인 타입: iOS / AOS 캠페인 ID 분리 입력 -->
                            <v-col v-else-if="dialog_link_type === 'CAMPAIGN'" cols="12" md="8">
                                <v-text-field
                                    v-model="dialog_campaign_id_aos"
                                    label="캠페인 ID (AOS)"
                                    outlined
                                    dense
                                    class="mb-3"
                                    placeholder="안드로이드 캠페인 ID"
                                    hint="AOS 캠페인 ID는 호환을 위해 링크 경로(LINK_PATH)에도 저장됩니다"
                                    persistent-hint
                                    prepend-inner-icon="mdi-android"
                                />
                                <v-text-field
                                    v-model="dialog_campaign_id_ios"
                                    label="캠페인 ID (iOS)"
                                    outlined
                                    dense
                                    placeholder="iOS 캠페인 ID"
                                    hint="AOS 캠페인 ID가 없으면 iOS 캠페인 ID가 링크 경로에 저장됩니다"
                                    persistent-hint
                                    prepend-inner-icon="mdi-apple"
                                />
                            </v-col>
                            <!-- 그 외(링크/플레이스먼트): 링크 경로 자유 입력 -->
                            <v-col v-else cols="12" md="8">
                                <v-text-field
                                    v-model="dialog_link_path"
                                    label="링크 경로"
                                    outlined
                                    dense
                                    placeholder="예: https://example.com 또는 /path/to/page"
                                    hint="링크 타입에 맞는 경로를 입력하세요"
                                    persistent-hint
                                    prepend-inner-icon="mdi-map-marker-path"
                                />
                            </v-col>
                        </v-row>
                        <!-- CAMPAIGN 타입일 때만 추가 파라미터 노출 -->
                        <v-row v-if="dialog_link_type === 'CAMPAIGN'">
                            <v-col cols="12">
                                <v-text-field
                                    v-model="dialog_sub_param"
                                    label="추가 파라미터 (sub_param)"
                                    outlined
                                    dense
                                    placeholder="예: 플레이스먼트 키"
                                    hint="CAMPAIGN 액션 시 함께 전달할 부가 파라미터. 비워두면 미사용."
                                    persistent-hint
                                    prepend-inner-icon="mdi-key-variant"
                                />
                            </v-col>
                        </v-row>
                    </div>

                    <!-- 우선순위 설정 섹션 -->
                    <div class="mb-6">
                        <div class="d-flex align-center mb-4">
                            <v-icon color="primary" class="mr-2">mdi-star-outline</v-icon>
                            <h3 class="text-h6 font-weight-bold">우선순위 설정</h3>
                        </div>
                        <v-divider class="mb-4"></v-divider>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="dialog_is_important"
                                    :items="yes_no_options"
                                    item-text="label"
                                    item-value="value"
                                    label="매우 중요"
                                    outlined
                                    dense
                                    prepend-inner-icon="mdi-alert"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model.number="dialog_priority"
                                    label="중요도 (1~10)"
                                    type="number"
                                    min="1"
                                    max="10"
                                    outlined
                                    dense
                                    prepend-inner-icon="mdi-numeric"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <div class="d-flex align-center" style="height: 56px;">
                                    <v-icon color="primary" class="mr-2">mdi-toggle-switch</v-icon>
                                    <div>
                                        <div class="text-caption grey--text text--darken-1 mb-1">활성화 여부</div>
                                        <v-switch
                                            v-model="dialog_enable_yn_switch"
                                            color="primary"
                                            :label="dialog_enable_yn_switch ? '활성화' : '비활성화'"
                                            inset
                                            hide-details
                                            class="mt-0"
                                        />
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </div>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey"
                        text
                        @click="CloseEventDialog"
                    >
                        취소
                    </v-btn>
                    <v-btn
                        color="primary"
                        @click="OnSave_Event"
                        :loading="loadingData"
                    >
                        저장
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import config from '../../config/config';
import store from '../../store';
import { OFFERWALL_KEYS } from '../../config/carrygold_config';
const { FileUpload_Async } = require("../../util/fileutil");

export default {
    name: 'Game_EventNoti',

    data: () => ({
        loadingData: false,
        event_list: [],
        dialog_event: false,
        dialog_mode_edit: false,
        dialog_thumb_file: null,
        dialog_thumb_url: '',
        dialog_title: '',
        dialog_desc_text: '',
        dialog_button_text: '',
        dialog_link_type: 'LINK',
        dialog_link_path: '',
        dialog_campaign_id_ios: '',   // CAMPAIGN 타입일 때만 사용
        dialog_campaign_id_aos: '',   // CAMPAIGN 타입일 때만 사용
        dialog_sub_param: '',   // CAMPAIGN 타입일 때만 사용
        dialog_is_important: 'N',
        dialog_priority: 1,
        dialog_enable_yn: 'Y',
        dialog_enable_yn_switch: true,
        selected_event_pid: null,

        headers: [
            { text: 'PID', value: 'PID', width: '80px' },
            { text: '썸네일', value: 'THUMB_URL', width: '100px' },
            { text: '타이틀', value: 'TITLE', width: '200px' },
            { text: '링크 타입', value: 'LINK_TYPE', width: '120px' },
            { text: '매우 중요', value: 'IS_IMPORTANT', width: '100px', align: 'center' },
            { text: '중요도', value: 'PRIORITY', width: '100px', align: 'center' },
            { text: '활성화', value: 'ENABLE_YN', width: '120px' },
            { text: '생성일', value: 'CREATE_DT', width: '180px' },
            { text: '편집', value: 'edit', width: '100px', align: 'center', sortable: false },
        ],

        link_type_options: [
            { label: '링크', value: 'LINK' },
            { label: '플레이스먼트', value: 'PLACEMENT' },
            { label: '캠페인', value: 'CAMPAIGN' },
            { label: '오퍼월', value: 'OFFERWALL' }
        ],

        // 오퍼월 링크 타입일 때 선택 가능한 값 (금털기 전용 config, 자유 입력 불가)
        offerwall_options: OFFERWALL_KEYS,

        yes_no_options: [
            { label: '예', value: 'Y' },
            { label: '아니오', value: 'N' }
        ],
    }),

    created() {
        this.LoadEventList();
    },

    methods: {
        ...mapActions([
            'CARRYGOLD_CREATE_EVENT',
            'CARRYGOLD_GET_EVENT_LIST',
            'CARRYGOLD_GET_EVENT',
            'CARRYGOLD_EDIT_EVENT',
        ]),

        // 링크 타입을 오퍼월로 바꾸면, 기존 자유입력 잔여값이 목록에 없을 때 비워
        // 목록에서 선택한 값만 저장되도록 한다. (@change 는 사용자 조작에만 발동 → 편집 로드에는 영향 없음)
        onLinkTypeChange(newType) {
            if (newType === 'OFFERWALL' && !this.offerwall_options.includes(this.dialog_link_path)) {
                this.dialog_link_path = '';
            }
        },

        LoadEventList() {
            this.loadingData = true;
            this.CARRYGOLD_GET_EVENT_LIST({}).then((body_data) => {
                this.loadingData = false;
                this.event_list = body_data.event_list || [];
                console.log('이벤트 리스트 로드:', this.event_list);
            })
            .catch((err) => {
                this.loadingData = false;
                console.error('이벤트 리스트 로드 실패:', err);
            });
        },

        OnClick_CreateEvent() {
            this.dialog_mode_edit = false;
            this.selected_event_pid = null;
            this.dialog_thumb_file = null;
            this.dialog_thumb_url = '';
            this.dialog_title = '';
            this.dialog_desc_text = '';
            this.dialog_button_text = '';
            this.dialog_link_type = 'LINK';
            this.dialog_link_path = '';
            this.dialog_campaign_id_ios = '';
            this.dialog_campaign_id_aos = '';
            this.dialog_sub_param = '';
            this.dialog_is_important = 'N';
            this.dialog_priority = 1;
            this.dialog_enable_yn = 'Y';
            this.dialog_enable_yn_switch = true;
            this.dialog_event = true;
        },

        OnClick_EditEvent(item) {
            this.dialog_mode_edit = true;
            this.selected_event_pid = item.PID;
            this.dialog_thumb_file = null;
            this.dialog_thumb_url = item.THUMB_URL;
            this.dialog_title = item.TITLE;
            this.dialog_desc_text = item.DESC_TEXT;
            this.dialog_button_text = item.BUTTON_TEXT;
            this.dialog_link_type = item.LINK_TYPE;
            this.dialog_link_path = item.LINK_PATH || '';
            // 캠페인 ID 채우기 (레거시 호환: 신규 컬럼이 비어있으면 LINK_PATH 를 AOS 로 간주)
            this.dialog_campaign_id_ios = item.CAMPAIGN_ID_IOS || '';
            this.dialog_campaign_id_aos = item.CAMPAIGN_ID_AOS || (item.LINK_TYPE === 'CAMPAIGN' ? (item.LINK_PATH || '') : '');
            this.dialog_sub_param = item.SUB_PARAM || '';
            this.dialog_is_important = item.IS_IMPORTANT;
            this.dialog_priority = item.PRIORITY;
            this.dialog_enable_yn = item.ENABLE_YN;
            this.dialog_enable_yn_switch = item.ENABLE_YN === 'Y';
            this.dialog_event = true;
        },

        handleFileChange(file) {
            // 파일 선택 시 처리
        },

        async OnSave_Event() {
            // 필수 필드 검증
            if (!this.dialog_title || !this.dialog_desc_text || !this.dialog_button_text) {
                alert('필수 항목을 모두 입력해주세요.');
                return;
            }

            // 캠페인 타입이면 AOS / iOS 캠페인 ID 중 최소 1개 필요
            if (this.dialog_link_type === 'CAMPAIGN' && !this.dialog_campaign_id_aos && !this.dialog_campaign_id_ios) {
                alert('캠페인 타입은 AOS 또는 iOS 캠페인 ID를 최소 1개 입력해야 합니다.');
                return;
            }

            if (!this.dialog_mode_edit && !this.dialog_thumb_file) {
                alert('썸네일 이미지를 선택해주세요.');
                return;
            }

            this.loadingData = true;

            // 스위치 값을 Y/N으로 변환
            this.dialog_enable_yn = this.dialog_enable_yn_switch ? 'Y' : 'N';

            let thumb_url = this.dialog_thumb_url;

            // 새 이미지가 업로드된 경우
            if (this.dialog_thumb_file) {
                // 파일 크기 체크 (1MB = 1048576 bytes)
                const maxFileSize = 1024 * 1024; // 1MB
                if (this.dialog_thumb_file.size > maxFileSize) {
                    this.loadingData = false;
                    alert('파일 크기가 1MB를 초과합니다. 더 작은 파일을 선택해주세요.');
                    return;
                }

                thumb_url = "project_carrygold/event_noti/images/" + this.Utill_CreateNewImageName(this.dialog_thumb_file.name);
                try {
                    await FileUpload_Async(this.dialog_thumb_file, thumb_url);
                } catch (error) {
                    this.loadingData = false;
                    console.error('이미지 업로드 실패:', error);
                    alert('이미지 업로드에 실패했습니다.');
                    return;
                }
            }

            // 캠페인이면 캠페인 ID를 분리 전송하고, 호환용 LINK_PATH = AOS(없으면 IOS)
            const isCampaign = this.dialog_link_type === 'CAMPAIGN';
            const campaign_id_ios = isCampaign ? (this.dialog_campaign_id_ios || '') : '';
            const campaign_id_aos = isCampaign ? (this.dialog_campaign_id_aos || '') : '';
            const link_path = isCampaign
                ? (this.dialog_campaign_id_aos || this.dialog_campaign_id_ios || '')
                : this.dialog_link_path;

            if (this.dialog_mode_edit) {
                // 수정 모드
                this.CARRYGOLD_EDIT_EVENT({
                    pid: this.selected_event_pid,
                    thumb_url: thumb_url,
                    link_type: this.dialog_link_type,
                    link_path: link_path,
                    campaign_id_ios: campaign_id_ios,
                    campaign_id_aos: campaign_id_aos,
                    // CAMPAIGN 타입일 때만 sub_param 값 전송 (다른 타입은 빈 문자열로 정리)
                    sub_param: this.dialog_link_type === 'CAMPAIGN' ? (this.dialog_sub_param || '') : '',
                    title: this.dialog_title,
                    desc_text: this.dialog_desc_text,
                    button_text: this.dialog_button_text,
                    is_important: this.dialog_is_important,
                    priority: this.dialog_priority,
                    enable_yn: this.dialog_enable_yn
                }).then((body_data) => {
                    this.loadingData = false;
                    alert('이벤트가 수정되었습니다.');
                    this.CloseEventDialog();
                    this.LoadEventList();
                })
                .catch((err) => {
                    this.loadingData = false;
                    console.error('이벤트 수정 실패:', err);
                    alert('이벤트 수정에 실패했습니다.');
                });
            } else {
                // 생성 모드
                this.CARRYGOLD_CREATE_EVENT({
                    thumb_url: thumb_url,
                    link_type: this.dialog_link_type,
                    link_path: link_path,
                    campaign_id_ios: campaign_id_ios,
                    campaign_id_aos: campaign_id_aos,
                    // CAMPAIGN 타입일 때만 sub_param 값 전송 (다른 타입은 빈 문자열로 정리)
                    sub_param: this.dialog_link_type === 'CAMPAIGN' ? (this.dialog_sub_param || '') : '',
                    title: this.dialog_title,
                    desc_text: this.dialog_desc_text,
                    button_text: this.dialog_button_text,
                    is_important: this.dialog_is_important,
                    priority: this.dialog_priority,
                    enable_yn: this.dialog_enable_yn
                }).then((body_data) => {
                    this.loadingData = false;
                    alert('이벤트가 등록되었습니다.');
                    this.CloseEventDialog();
                    this.LoadEventList();
                })
                .catch((err) => {
                    this.loadingData = false;
                    console.error('이벤트 생성 실패:', err);
                    alert('이벤트 등록에 실패했습니다.');
                });
            }
        },

        CloseEventDialog() {
            this.dialog_event = false;
            this.dialog_mode_edit = false;
            this.selected_event_pid = null;
        },

        Utill_CreateNewImageName(originalFileName) {
            const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));
            const randomString = Math.random().toString(36).substring(2, 18);
            return randomString + fileExtension;
        },

        getLinkTypeLabel(type) {
            const labels = {
                'LINK': '링크',
                'CAMPAIGN': '캠페인',
                'PLACEMENT': '플레이스먼트',
                'OFFERWALL': '오퍼월'
            };
            return labels[type] || type;
        },

        getLinkTypeColor(type) {
            const colors = {
                'LINK': 'blue',
                'PLACEMENT': 'purple',
                'CAMPAIGN': 'orange',
                'OFFERWALL': 'teal'
            };
            return colors[type] || 'grey';
        },

        getPriorityColor(priority) {
            if (priority >= 8) return 'red';
            if (priority >= 5) return 'orange';
            return 'green';
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    },

    computed: {
        isLive() {
            return config.isLive;
        }
    }
};
</script>

<style scoped>
@media (max-width: 600px) {
  .v-data-table { overflow-x: auto; }
}
</style>

